import { defineStore } from "pinia";
import { loadVisibleMatsFromLocalStorage, saveVisibleMatsToLocalStorage } from "./scheduleManageApp";
import { useScheduleMainStore } from "./useScheduleMainStore";
import schedulesApi, {
    NamedScheduledCategory,
    MatSchedule,
    ScheduledCategoryTime,
    MatSchedulesResult,
    MatScheduleSummary,
} from "../../Api/schedules";
import { handleError } from "../../Common/errorHandler";
import { DropZoneItem } from "./dropZoneItems";
import objectMatcher from "../../Common/objectMatcher";
import { translate } from "../../Common/translations";
import { getRoundMarkerTranslationKey, parseRoundMarker } from "./dropZoneItems";

function getBlockedScheduledCategories(scheduledCategories: NamedScheduledCategory[]): NamedScheduledCategory[] {
    return scheduledCategories.filter(sc => !sc.canBeMoved);
}

function getMovableScheduledCategories(scheduledCategories: NamedScheduledCategory[]): NamedScheduledCategory[] {
    return scheduledCategories.filter(sc => sc.canBeMoved);
}

function searchableScheduledCategoryText(scheduledCategory: NamedScheduledCategory): string {
    return [
        scheduledCategory.category,
        ...[scheduledCategory.roundMarkerFrom, scheduledCategory.roundMarkerTo]
            .map(parseRoundMarker)
            .filter((marker): marker is NonNullable<typeof marker> => marker !== null)
            .map(marker => translate(getRoundMarkerTranslationKey(marker)) ?? marker),
    ].join(" ");
}

const DEFAULT_CONFLICTS_THRESHOLD = 1800;

type SelectionContext = {
    type: "mat" | "dropzone";
    matId?: number;
} | null;

export const useShowScheduleStore = defineStore("showSchedule", {
    state: () => ({
        conflictedScheduledCategoryIds: {} as Record<number, boolean>,
        conflictsMapping: {} as MatSchedulesResult["conflicts"]["mapping"],
        highlightedConflicts: {} as Record<number, boolean>,
        conflictsArePresent: false,

        startTimes: {} as Record<number, Record<number, ScheduledCategoryTime>>,
        /** Mat id => MatSchedule */
        matSchedules: {} as Record<number, MatSchedule>,
        matScheduleSummaries: {} as Record<number, MatScheduleSummary>,
        /** Mat id => array of scheduled categories that cannot be moved */
        blockedScheduledCategories: {} as Record<number, NamedScheduledCategory[]>,
        /** Mat id => array of scheduled categories that can be moved */
        movableScheduledCategories: {} as Record<number, NamedScheduledCategory[]>,
        matVisibility: {} as Record<number, boolean>,
        scheduledCategorySearchQuery: "",

        matAdded: { matId: 0, newIndex: null as number | null },
        matRemoved: { matId: 0, oldIndex: null as number | null, scheduledCategoryIds: [] as number[] },

        dropZoneAdded: { newIndex: null as number | null },
        dropZoneRemoved: { dropZoneItems: [] as DropZoneItem[], oldIndex: null as number | null },

        conflictsThreshold: DEFAULT_CONFLICTS_THRESHOLD,
        multiRowMode: false,
        editMode: false,
        /** True while a category row is being dragged; lets mats highlight themselves as drop targets. */
        isDraggingCategory: false,
        /** Row just dropped onto a mat; the mat scrolls to and highlights the rows with new ids once its refreshed list is rendered. */
        pendingMatScroll: null as { matId: number; knownIds: number[] } | null,

        // Either DropZone item keys or scheduledCategoryIds from mats
        selectedItems: new Set<number | string>(),
        activeSelectionContext: null as SelectionContext,
    }),

    getters: {
        matchingScheduledCategoryIds(state): Record<number, boolean> {
            if (state.scheduledCategorySearchQuery.trim() === "") {
                return {};
            }

            const matches: Record<number, boolean> = {};
            Object.entries(state.matSchedules).forEach(([matId, matSchedule]) => {
                if (!state.matVisibility[Number(matId)]) {
                    return;
                }

                matSchedule.scheduledCategories.forEach(scheduledCategory => {
                    if (objectMatcher.matchString(state.scheduledCategorySearchQuery, searchableScheduledCategoryText(scheduledCategory))) {
                        matches[scheduledCategory.id] = true;
                    }
                });
            });

            return matches;
        },
    },

    actions: {
        /**
         * Clears everything that belongs to the previously managed schedule.
         * Per-event preferences (conflicts threshold, multi-row and edit mode) are intentionally kept.
         */
        resetForSchedule() {
            this.conflictedScheduledCategoryIds = {};
            this.conflictsMapping = {};
            this.highlightedConflicts = {};
            this.conflictsArePresent = false;
            this.startTimes = {};
            this.matSchedules = {};
            this.matScheduleSummaries = {};
            this.blockedScheduledCategories = {};
            this.movableScheduledCategories = {};
            this.matVisibility = {};
            this.scheduledCategorySearchQuery = "";
            this.matAdded = { matId: 0, newIndex: null };
            this.matRemoved = { matId: 0, oldIndex: null, scheduledCategoryIds: [] };
            this.dropZoneAdded = { newIndex: null };
            this.dropZoneRemoved = { dropZoneItems: [], oldIndex: null };
            this.isDraggingCategory = false;
            this.pendingMatScroll = null;
            this.clearSelection();
        },
        findSelectedDropZoneItems(): DropZoneItem[] {
            if (this.activeSelectionContext?.type !== "dropzone") {
                return [];
            }

            const mainStore = useScheduleMainStore();
            const dropZoneItemsMap = new Map(mainStore.dropZoneItems.map(item => [item.key, item]));
            const selectedDropZoneItems: DropZoneItem[] = [];

            this.selectedItems.forEach(itemKey => {
                const item = dropZoneItemsMap.get(String(itemKey));
                if (item) {
                    selectedDropZoneItems.push(item);
                }
            });

            return selectedDropZoneItems;
        },
        getAllScheduledCategories(): NamedScheduledCategory[] {
            return Object.values(this.matSchedules).flatMap(matSchedule => matSchedule.scheduledCategories);
        },
        findSelectedScheduledCategory(): NamedScheduledCategory | null {
            const matId = this.activeSelectionContext?.matId ?? 0;

            const matSchedule = this.matSchedules[matId];
            if (!matSchedule) {
                return null;
            }

            for (const sc of matSchedule.scheduledCategories) {
                if (this.isSelected(sc.id)) {
                    return sc;
                }
            }

            return null;
        },

        setMatVisibility(matId: number, visible: boolean) {
            const mainStore = useScheduleMainStore();
            this.matVisibility[matId] = visible;
            saveVisibleMatsToLocalStorage(mainStore.eventId, this.matVisibility);
        },

        deleteMatVisibility(matId: number) {
            const mainStore = useScheduleMainStore();
            if (this.matVisibility) {
                delete this.matVisibility[matId];
            }
            saveVisibleMatsToLocalStorage(mainStore.eventId, this.matVisibility);
        },

        setConflictedScheduledCategoryIds(ids: number[]) {
            const asMap: Record<number, boolean> = {};
            ids.forEach(id => {
                asMap[id] = true;
            });
            this.conflictedScheduledCategoryIds = asMap;
        },

        setHighlightedConflicts(ids: number[]) {
            const asMap: Record<number, boolean> = {};
            ids.forEach(id => {
                asMap[id] = true;
            });
            this.highlightedConflicts = asMap;
        },

        updateStateOfSingleMatSchedule(state: MatSchedulesResult, matId: number, visible: boolean) {
            this.matSchedules[matId] = state.matSchedules[matId];
            this.matScheduleSummaries[matId] = state.matScheduleSummaries[matId];
            this.startTimes[matId] = state.startTimes[matId];
            this.setMatVisibility(matId, visible);
            this.blockedScheduledCategories[matId] = getBlockedScheduledCategories(state.matSchedules[matId].scheduledCategories);
            this.movableScheduledCategories[matId] = getMovableScheduledCategories(state.matSchedules[matId].scheduledCategories);
        },

        setConflictedScheduledCategoryIdsFromState(state: MatSchedulesResult) {
            this.setHighlightedConflicts([]);
            this.setConflictedScheduledCategoryIds(state.conflicts.scheduledCategoryIds);
            this.conflictsArePresent = state.conflicts.present;
            this.conflictsMapping = state.conflicts.mapping;
        },

        /**
         * Updates the store state from a MatSchedulesResult response.
         * @param result The API response containing mat schedules and conflicts
         * @param getMatVisibility Function to determine visibility for each mat
         */
        updateStateFromMatSchedulesResult(result: MatSchedulesResult, getMatVisibility: (matId: number) => boolean) {
            // Update conflicts once (they're global to the result, not per-mat)
            this.setConflictedScheduledCategoryIdsFromState(result);

            // Update each mat schedule
            for (const matIdStr of Object.keys(result.matSchedules)) {
                const matId = Number(matIdStr);
                const visible = getMatVisibility(matId);
                this.updateStateOfSingleMatSchedule(result, matId, visible);
            }
        },
        /**
         * Removes multiple scheduled categories from a mat.
         */
        removeScheduledCategories(scheduledCategoryIds: number[]) {
            const mainStore = useScheduleMainStore();
            const matVisibilityFromLocalStorage = loadVisibleMatsFromLocalStorage(mainStore.eventId) || {};

            return mainStore
                .trackRequest(
                    schedulesApi.removeScheduledCategories(
                        mainStore.eventId,
                        mainStore.scheduleId,
                        { scheduledCategoryIds },
                        this.conflictsThreshold,
                    ),
                )
                .then((result: MatSchedulesResult) => {
                    this.updateStateFromMatSchedulesResult(result, _matId => matVisibilityFromLocalStorage[_matId]);

                    return mainStore.refreshDropZoneItems();
                });
        },
        refreshAllMatSchedules() {
            const mainStore = useScheduleMainStore();
            const matVisibilityFromLocalStorage = loadVisibleMatsFromLocalStorage(mainStore.eventId) || {};

            return mainStore
                .trackRequest(schedulesApi.getAllMatSchedules(mainStore.eventId, mainStore.scheduleId, [], this.conflictsThreshold))
                .then((result: MatSchedulesResult) => {
                    this.updateStateFromMatSchedulesResult(result, matId => matVisibilityFromLocalStorage[matId]);
                })
                .catch(handleError);
        },
        refreshSingleMatSchedule(matIds: number[]) {
            const mainStore = useScheduleMainStore();

            return mainStore
                .trackRequest(schedulesApi.getAllMatSchedules(mainStore.eventId, mainStore.scheduleId, matIds, this.conflictsThreshold))
                .then((result: MatSchedulesResult) => {
                    this.updateStateFromMatSchedulesResult(result, () => true);
                })
                .catch(handleError);
        },

        // Selection management
        setActiveContext(type: "mat" | "dropzone", matId?: number) {
            const newContext: SelectionContext = { type, matId };

            // If context changes, clear selection
            if (
                !this.activeSelectionContext ||
                this.activeSelectionContext.type !== newContext.type ||
                this.activeSelectionContext.matId !== newContext.matId
            ) {
                this.selectedItems = new Set();
            }

            this.activeSelectionContext = newContext;
        },

        isSelected(itemId: number | string): boolean {
            return this.selectedItems.has(itemId);
        },

        clearSelection() {
            this.selectedItems = new Set();
            this.activeSelectionContext = null;
        },

        /**
         * @param itemId Either scheduled category ID or parameter ID.
         */
        toggleSingleSelection(itemId: number | string) {
            if (this.selectedItems.has(itemId)) {
                this.selectedItems.delete(itemId);
            } else {
                this.selectedItems.add(itemId);
            }
            // Trigger reactivity by creating new Set
            this.selectedItems = new Set(this.selectedItems);
        },

        /**
         * @param itemId Either scheduled category ID or parameter ID.
         */
        setSingleSelection(itemId: number | string) {
            this.selectedItems = new Set([itemId]);
        },

        /**
         * @param itemIds Either scheduled category IDs or parameter IDs.
         */
        selectRange(itemIds: Array<number | string>) {
            itemIds.forEach(id => this.selectedItems.add(id));
            // Trigger reactivity
            this.selectedItems = new Set(this.selectedItems);
        },

        moveSelectedToDropzone() {
            // Only proceed if we have a mat context
            if (!this.activeSelectionContext || this.activeSelectionContext.type !== "mat") {
                return Promise.resolve();
            }

            const selectedIds = Array.from(this.selectedItems).filter((id): id is number => typeof id === "number");

            if (selectedIds.length === 0) {
                return Promise.resolve();
            }

            // Remove selected categories from the mat (moves them to dropzone)
            return this.removeScheduledCategories(selectedIds).then(() => {
                this.clearSelection();
            });
        },
    },
});
